import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import { computeQuotation, COST_CURRENCIES } from '../../lib/docgen/pricing';
import { quotationHtml, rfqHtml, fmtNum, PHOTO_SIZES } from '../../lib/docgen/render';
import { withDefaults } from '../../lib/docgen/content';
import { loadDocument, saveDraft, copyDocument, deleteDraft, newId } from '../../lib/docgen/store';
import {
  addImage, cropImage, updateImage, removeImages, signedUrls, importFromDocument, importFromRequest,
  MAX_LINE_IMAGES, MAX_GALLERY_IMAGES,
} from '../../lib/docgen/images';
import { ImageDrop, Thumb, PickerModal, CropModal } from './docgen/ImageControls';
import { draftPdf, approveAndExport, downloadStoredPdf } from '../../lib/docgen/exportPdf';

// Document generator — the editor: form, live preview, and (quotations only)
// the admin's profit panel. Drafts save themselves; an approved document opens
// read-only and changes through "Create revision".
//
// ⚠️ The preview is built from pricing's clientView only. The profit panel
// reads internalView, which never reaches the renderer — see pricing.js.
// This page writes only doc_* rows (store.js); never requests or offers.

const ERR = {
  missing_cost: ['سعر المورد ناقص', 'Supplier price missing'],
  missing_cost_currency: ['عملة سعر المورد ناقصة', 'Cost currency missing'],
  missing_qty: ['الكمية ناقصة', 'Quantity missing'],
  missing_fx_cny_usd: ['سعر صرف اليوان ← الدولار مطلوب', 'CNY → USD rate required'],
  missing_fx_usd_sar: ['سعر صرف الدولار ← الريال مطلوب', 'USD → SAR rate required'],
  itemized_units_mismatch: ['كل الأقسام يجب أن تكون بنفس عدد الوحدات', 'All sections must share the same unit count'],
  empty_section: ['قسم بلا بنود ولا تكلفة إجمالية', 'Section has no lines and no lump cost'],
  paid_exceeds_deposit: ['المبلغ المدفوع أكبر من الدفعة المقدمة', 'Amount paid exceeds the deposit'],
  bad_currency: ['عملة العرض غير صالحة', 'Invalid quote currency'],
  orphan_line: ['بند خارج أي قسم', 'Line outside any section'],
};

const A4_PX = { portrait: 794, landscape: 1123 };
const todayIso = () => new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 10); // Riyadh

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

// ── small form atoms ────────────────────────────────────────────────────
function Field({ label, children, wide }) {
  return <label className={`ac-field${wide ? ' wide' : ''}`}><span className="ac-flabel">{label}</span>{children}</label>;
}
function Card({ title, children, right }) {
  return (
    <div className="ac-card" style={{ padding: 16, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ac-ink)' }}>{title}</div>{right}
      </div>
      {children}
    </div>
  );
}
const miniBtn = { height: 28, minWidth: 28, padding: '0 8px', fontSize: 12 };

// Reorderable list of plain strings, edited one per line.
function LinesText({ value, onChange, disabled, rows = 3, dir }) {
  return (
    <textarea className="ac-textarea" rows={rows} disabled={disabled} dir={dir}
      value={(value || []).join('\n')} onChange={(e) => onChange(e.target.value.split('\n'))} />
  );
}

export default function ConsoleDocEditor({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const t = useCallback((ar, en) => (isAr ? ar : en), [isAr]);
  const { id } = useParams();
  const nav = useNavigate();

  const [doc, setDoc] = useState(null);
  const [sections, setSections] = useState([]);
  const [lines, setLines] = useState([]);
  const [images, setImages] = useState([]);
  const [loadErr, setLoadErr] = useState('');
  const [save, setSave] = useState({ state: 'saved', error: '' }); // saved | pending | saving | error
  const [toast, setToast] = useState('');
  const [urls, setUrls] = useState({});        // storage_path → signed URL (preview only)
  const [picker, setPicker] = useState(null);  // { lineId } | null
  const [cropping, setCropping] = useState(null); // image row | null
  const [imgBusy, setImgBusy] = useState(false);
  const [exporting, setExporting] = useState('');   // '' | 'draft' | 'final' | 'download'
  const [approveOpen, setApproveOpen] = useState(false);
  const [ackImages, setAckImages] = useState(false);
  const [exportErr, setExportErr] = useState('');
  const deleted = useRef({ sections: new Set(), lines: new Set() });
  const version = useRef(0);   // bumps on every edit
  const savedVersion = useRef(0);
  const saving = useRef(false);

  useEffect(() => {
    let alive = true;
    loadDocument(id).then((r) => {
      if (!alive) return;
      setDoc(r.doc); setSections(r.sections); setLines(r.lines); setImages(r.images);
      signedUrls(r.images.map((i) => i.storage_path)).then((u) => alive && setUrls(u)).catch(() => {});
    }).catch((e) => alive && setLoadErr(e.message || 'error'));
    return () => { alive = false; };
  }, [id]);

  const locked = doc && doc.status !== 'draft';
  const isRfq = doc?.kind === 'rfq';
  const content = useMemo(() => (doc ? withDefaults(doc.kind, doc.content) : null), [doc]);

  // ── edits ──
  const touch = () => { version.current += 1; setSave((s) => ({ ...s, state: 'pending' })); };
  const setField = (k, v) => { if (locked) return; setDoc((d) => ({ ...d, [k]: v })); touch(); };
  const setC = (k, v) => { if (locked) return; setDoc((d) => ({ ...d, content: { ...withDefaults(d.kind, d.content), [k]: v } })); touch(); };
  const setSection = (sid, patch) => { if (locked) return; setSections((ss) => ss.map((s) => (s.id === sid ? { ...s, ...patch } : s))); touch(); };
  const setLine = (lid, patch) => { if (locked) return; setLines((ls) => ls.map((l) => (l.id === lid ? { ...l, ...patch } : l))); touch(); };
  const addSection = () => {
    if (locked) return;
    const units = sections[0]?.units || 1;
    setSections((ss) => [...ss, { id: newId(), title: '', units, lump_cost: null, lump_currency: null }]); touch();
  };
  const removeSection = (sid) => {
    if (locked || sections.length <= 1) return;
    deleted.current.sections.add(sid);
    const gone = new Set(lines.filter((l) => l.section_id === sid).map((l) => l.id));
    gone.forEach((x) => deleted.current.lines.add(x));
    const own = images.filter((im) => gone.has(im.line_id));
    if (own.length) removeImages(own).then(() => setImages((xs) => xs.filter((x) => !gone.has(x.line_id)))).catch((e) => flash(e.message));
    setSections((ss) => ss.filter((s) => s.id !== sid));
    setLines((ls) => ls.filter((l) => l.section_id !== sid)); touch();
  };
  const moveSection = (sid, dir) => {
    if (locked) return;
    setSections((ss) => { const i = ss.findIndex((s) => s.id === sid); const j = i + dir; if (j < 0 || j >= ss.length) return ss;
      const n = [...ss]; [n[i], n[j]] = [n[j], n[i]]; return n; }); touch();
  };
  const addLine = (sid) => {
    if (locked) return;
    setLines((ls) => [...ls, { id: newId(), section_id: sid, item: '', description: '', brand: '', unit: '', qty: '',
      supplier_unit_cost: '', cost_currency: 'USD', line_kind: 'goods' }]); touch();
  };
  const removeLine = (lid) => {
    if (locked) return;
    const own = images.filter((im) => im.line_id === lid);
    if (own.length) removeImages(own).then(() => setImages((xs) => xs.filter((x) => x.line_id !== lid))).catch((e) => flash(e.message));
    deleted.current.lines.add(lid); setLines((ls) => ls.filter((l) => l.id !== lid)); touch();
  };
  const moveLine = (lid, dir) => {
    if (locked) return;
    setLines((ls) => {
      const l = ls.find((x) => x.id === lid);
      const same = ls.filter((x) => x.section_id === l.section_id);
      const i = same.indexOf(l); const j = i + dir;
      if (j < 0 || j >= same.length) return ls;
      const other = same[j];
      return ls.map((x) => (x.id === l.id ? other : x.id === other.id ? l : x));
    }); touch();
  };
  // Itemized: one unit count for the whole document, written to every section.
  const setUnits = (v) => { if (locked) return; setSections((ss) => ss.map((s) => ({ ...s, units: v }))); touch(); };

  // ── autosave ──
  const runSave = useCallback(async () => {
    if (!doc || locked || saving.current || version.current === savedVersion.current) return;
    saving.current = true;
    const v = version.current;
    const del = { sections: [...deleted.current.sections], lines: [...deleted.current.lines] };
    setSave({ state: 'saving', error: '' });
    try {
      await saveDraft({ doc, sections, lines, deletedSectionIds: del.sections, deletedLineIds: del.lines });
      del.sections.forEach((x) => deleted.current.sections.delete(x));
      del.lines.forEach((x) => deleted.current.lines.delete(x));
      savedVersion.current = v;
      setSave({ state: version.current === v ? 'saved' : 'pending', error: '' });
    } catch (e) {
      setSave({ state: 'error', error: e.message || 'error' });
    }
    saving.current = false;
  }, [doc, sections, lines, locked]);

  useEffect(() => {
    if (save.state !== 'pending') return undefined;
    const tm = setTimeout(runSave, 900);
    return () => clearTimeout(tm);
  }, [save.state, doc, sections, lines, runSave]);

  // Warn before leaving with unsaved edits.
  useEffect(() => {
    const h = (e) => { if (save.state !== 'saved') { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [save.state]);

  // ── images ──
  // Signed URLs expire after an hour; refresh them for long sessions.
  useEffect(() => {
    const tm = setInterval(() => {
      signedUrls(images.map((i) => i.storage_path)).then(setUrls).catch(() => {});
    }, 50 * 60 * 1000);
    return () => clearInterval(tm);
  }, [images]);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 5000); };
  const imgError = (e) => (e?.message === 'unsupported_image'
    ? t('صيغة الصورة غير مدعومة (مثل HEIC من الآيفون). صدّرها بصيغة JPEG أو PNG ثم أعد المحاولة.', 'Unsupported image format (e.g. iPhone HEIC). Export it as JPEG or PNG and try again.')
    : (e?.message || 'error'));

  // A line image references the line, so the line must be saved first.
  const ensureSaved = async () => {
    for (let i = 0; i < 50 && saving.current; i += 1) await new Promise((r) => setTimeout(r, 100));
    if (version.current !== savedVersion.current) await runSave();
  };
  const groupOf = (lineId) => images.filter((im) => (im.line_id || null) === (lineId || null))
    .sort((a, b) => (a.position || 0) - (b.position || 0));
  const addSigned = async (rows) => {
    const u = await signedUrls(rows.map((r) => r.storage_path));
    setUrls((prev) => ({ ...prev, ...u }));
  };

  const addFiles = async (files, lineId = null) => {
    if (locked) return;
    const cap = lineId ? MAX_LINE_IMAGES : MAX_GALLERY_IMAGES;
    const room = cap - groupOf(lineId).length;
    if (room <= 0) { flash(t(`الحد الأقصى ${cap} صور هنا.`, `At most ${cap} images here.`)); return; }
    setImgBusy(true);
    try {
      if (lineId) await ensureSaved();
      let pos = groupOf(lineId).reduce((m, x) => Math.max(m, x.position || 0), -1) + 1;
      for (const file of files.slice(0, room)) {
        const row = await addImage({ docId: doc.id, lineId, file, position: pos });
        pos += 1;
        setImages((xs) => [...xs, row]);
        await addSigned([row]);
      }
      if (files.length > room) flash(t(`أُضيفت ${room} فقط — الحد الأقصى ${cap}.`, `Only ${room} added — the limit is ${cap}.`));
    } catch (e) { flash(imgError(e)); }
    setImgBusy(false);
  };
  const pickFromDoc = async (source, lineId) => {
    if (lineId) await ensureSaved();
    const row = await importFromDocument({ source, docId: doc.id, lineId, position: groupOf(lineId).length });
    setImages((xs) => [...xs, row]); await addSigned([row]);
  };
  const pickFromRequest = async (path, lineId) => {
    if (lineId) await ensureSaved();
    const row = await importFromRequest({ path, docId: doc.id, lineId, position: groupOf(lineId).length });
    setImages((xs) => [...xs, row]); await addSigned([row]);
  };
  const removeImg = async (im) => {
    try { await removeImages([im]); setImages((xs) => xs.filter((x) => x.id !== im.id)); } catch (e) { flash(e.message); }
  };
  const patchImg = async (im, patch, persist = true) => {
    setImages((xs) => xs.map((x) => (x.id === im.id ? { ...x, ...patch } : x)));
    if (persist) { try { await updateImage(im.id, patch); } catch (e) { flash(e.message); } }
  };
  const moveImg = async (im, dir) => {
    const g = groupOf(im.line_id);
    const i = g.findIndex((x) => x.id === im.id); const j = i + dir;
    if (j < 0 || j >= g.length) return;
    const next = [...g]; [next[i], next[j]] = [next[j], next[i]];
    setImages((xs) => xs.map((x) => { const k = next.findIndex((y) => y.id === x.id); return k >= 0 ? { ...x, position: k } : x; }));
    try { await Promise.all(next.map((x, k) => updateImage(x.id, { position: k }))); } catch (e) { flash(e.message); }
  };
  const applyCrop = async (im, area) => {
    const row = await cropImage(im, area);
    setImages((xs) => xs.map((x) => (x.id === im.id ? row : x)));
    await addSigned([row]);
  };
  const withSrc = useMemo(() => images.map((im) => ({ ...im, src: urls[im.storage_path] })), [images, urls]);

  // ── pricing + preview ──
  const priced = useMemo(() => (doc && !isRfq ? computeQuotation(doc, sections, lines) : null), [doc, sections, lines, isRfq]);
  // Memoised so the debounce sees a new value only when an input really changed.
  const previewSource = useMemo(() => ({ doc, sections, lines, images: withSrc, priced }), [doc, sections, lines, withSrc, priced]);
  const previewInput = useDebounced(previewSource, 250);
  const html = useMemo(() => {
    const { doc: d, sections: s, lines: l, images: im, priced: p } = previewInput;
    if (!d) return '';
    const common = { doc: d, images: im.filter((x) => x.src), assetBase: '/docgen/', today: todayIso() };
    return d.kind === 'rfq' ? rfqHtml({ ...common, sections: s, lines: l }) : quotationHtml({ ...common, clientView: p.clientView });
  }, [previewInput]);

  // ── actions ──
  const act = async (mode) => {
    try {
      if (save.state !== 'saved' && !locked) await runSave();
      const nid = await copyDocument(doc.id, mode);
      nav(`/admin2/docs/${nid}`);
    } catch (e) { setToast(e.message); setTimeout(() => setToast(''), 4000); }
  };
  const remove = async () => {
    if (!window.confirm(t('حذف هذه المسودة نهائياً؟', 'Delete this draft permanently?'))) return;
    try { await deleteDraft(doc.id); nav('/admin2/docs'); } catch (e) { setToast(e.message); }
  };

  // ── PDF ──
  const reload = async () => {
    const r = await loadDocument(doc.id);
    setDoc(r.doc); setSections(r.sections); setLines(r.lines); setImages(r.images);
  };
  const openBlob = (blob) => {
    const u = URL.createObjectURL(blob);
    window.open(u, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(u), 60000);
  };
  const onDraftPdf = async () => {
    setExporting('draft');
    try {
      if (!locked) await ensureSaved();
      const { blob, warning } = await draftPdf(doc.id);
      openBlob(blob);
      if (warning) flash(warning);
    } catch (e) { flash(e.message); }
    setExporting('');
  };
  const onApprove = async () => {
    setExporting('final'); setExportErr('');
    try {
      await ensureSaved();
      const out = await approveAndExport(doc.id);
      await reload();
      setApproveOpen(false); setAckImages(false);
      flash(t(`تم الاعتماد: ${out.ref_no}`, `Approved: ${out.ref_no}`) + (out.warn ? ` — ${out.warn}` : ''));
    } catch (e) {
      setExportErr(e.status === 422 ? t('التسعير غير مكتمل — راجع لوحة الربح.', 'Pricing is incomplete — see the profit panel.') : e.message);
      await reload().catch(() => {}); // a failed render may have left it 'approving' with its number
    }
    setExporting('');
  };
  const onDownload = async () => {
    setExporting('download');
    try { window.location.assign(await downloadStoredPdf(doc)); } catch (e) { flash(e.message); }
    setExporting('');
  };

  if (loadErr) {
    return (<ConsoleShell user={user} profile={profile} lang={lang} active="docs">
      <div className="ac-page"><div className="ac-placeholder" style={{ color: 'var(--ac-danger)' }}>{loadErr}</div></div>
    </ConsoleShell>);
  }
  if (!doc) {
    return (<ConsoleShell user={user} profile={profile} lang={lang} active="docs"><div className="ac-page"><div className="ac-skel" style={{ height: 200 }} /></div></ConsoleShell>);
  }

  const c = content;
  const itemized = doc.layout === 'itemized';
  const cur = doc.currency;
  const iv = priced?.internalView;
  const errors = priced?.errors || [];
  const saveLabel = { saved: t('محفوظ', 'Saved'), pending: t('تعديلات غير محفوظة…', 'Unsaved changes…'), saving: t('جارٍ الحفظ…', 'Saving…'), error: t('تعذّر الحفظ', 'Save failed') }[save.state];
  const linkLabel = doc.requests ? `${t('طلب', 'Request')} ${doc.requests.request_ref}` : doc.doc_clients ? `${t('عميل واتساب', 'WhatsApp client')}: ${doc.doc_clients.name}` : t('بدون ربط', 'Standalone');
  const pageW = isRfq ? A4_PX.landscape : A4_PX.portrait;
  const inputNum = (k, step = 'any') => (
    <input className="ac-input" type="number" step={step} dir="ltr" disabled={locked} value={doc[k] ?? ''} onChange={(e) => setField(k, e.target.value)} />
  );

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="docs">
      <div className="ac-page" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', maxWidth: 'none' }}>
        {/* header */}
        <div className="ac-page-head" style={{ alignItems: 'flex-start' }}>
          <div>
            <button className="ac-back" onClick={() => nav('/admin2/docs')}>{isAr ? '→ المستندات' : '← Documents'}</button>
            <h1 className="ac-h1" style={{ marginTop: 6 }}>
              {isRfq ? t('طلب تسعير (RFQ)', 'Supplier RFQ') : itemized ? t('عرض سعر — مفصّل', 'Quotation — itemized') : t('عرض سعر', 'Quotation')}
              <span dir="ltr" style={{ marginInlineStart: 10, fontSize: 15, color: 'var(--ac-muted)', fontVariantNumeric: 'tabular-nums' }}>{doc.ref_no || 'DRAFT'}</span>
            </h1>
            <p className="ac-sub">{linkLabel} · <span style={{ color: save.state === 'error' ? 'var(--ac-danger)' : undefined }}>{locked ? t('معتمد — للقراءة فقط', 'Approved — read-only') : saveLabel}</span></p>
            {save.error && <p className="ac-sub" style={{ color: 'var(--ac-danger)' }}>{save.error}</p>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {doc.status === 'draft' && <>
              <button className="ac-btn ac-btn-sm" disabled={!!exporting || errors.length > 0} onClick={onDraftPdf}
                title={errors.length ? t('أكمل التسعير أولاً', 'Complete the pricing first') : ''}>
                {exporting === 'draft' ? '…' : t('PDF تجريبي', 'Draft PDF')}
              </button>
              <button className="ac-btn ac-btn-sm ac-btn-primary" disabled={!!exporting || errors.length > 0} onClick={() => { setExportErr(''); setApproveOpen(true); }}>
                {t('اعتماد وتصدير', 'Approve & export')}
              </button>
            </>}
            {doc.status === 'approving' && (
              <button className="ac-btn ac-btn-sm ac-btn-primary" disabled={!!exporting} onClick={() => { setExportErr(''); setApproveOpen(true); }}>
                {t('إكمال الاعتماد (إعادة المحاولة)', 'Finish approval (retry)')}
              </button>
            )}
            {doc.pdf_path && (
              <button className="ac-btn ac-btn-sm ac-btn-primary" disabled={!!exporting} onClick={onDownload}>
                {exporting === 'download' ? '…' : t('تحميل PDF', 'Download PDF')}
              </button>
            )}
            <button className="ac-btn ac-btn-sm" onClick={() => act('duplicate')}>{t('نسخ كمستند جديد', 'Duplicate')}</button>
            {doc.status !== 'draft' && <button className="ac-btn ac-btn-sm" onClick={() => act('revise')}>{t('إنشاء نسخة محدثة', 'Create revision')}</button>}
            {isRfq && <button className="ac-btn ac-btn-sm" onClick={() => act('quote_from_rfq')}>{t('إنشاء عرض سعر من هذا الطلب', 'Create quotation from this RFQ')}</button>}
            {!locked && <button className="ac-btn ac-btn-sm" onClick={remove} style={{ color: 'var(--ac-danger)' }}>{t('حذف المسودة', 'Delete draft')}</button>}
          </div>
        </div>

        <div className="dg-grid">
          {/* ── FORM ── */}
          <div style={{ minWidth: 0 }}>
            {!isRfq && (
              <Card title={t('العميل', 'Client')}>
                <div className="ac-form-grid">
                  <Field label={t('مقدم إلى', 'Addressed to')}><input className="ac-input" disabled={locked} value={c.clientName} onChange={(e) => setC('clientName', e.target.value)} /></Field>
                  <Field label={t('الموضوع', 'Subject')}><input className="ac-input" disabled={locked} value={c.subject} onChange={(e) => setC('subject', e.target.value)} /></Field>
                </div>
              </Card>
            )}

            {isRfq ? (
              <Card title={t('رأس الطلب', 'Header')}>
                <div className="ac-form-grid">
                  <Field label={t('العنوان (إنجليزي)', 'Title')} wide><input className="ac-input" dir="ltr" disabled={locked} value={c.title} onChange={(e) => setC('title', e.target.value)} /></Field>
                  <Field label={t('سطر عربي تحت العنوان', 'Arabic subtitle')} wide><input className="ac-input" dir="rtl" disabled={locked} value={c.arSubtitle} onChange={(e) => setC('arSubtitle', e.target.value)} /></Field>
                  <Field label={t('النطاق (Scope)', 'Scope')} wide><textarea className="ac-textarea" dir="ltr" rows={3} disabled={locked} value={c.scopeText} onChange={(e) => setC('scopeText', e.target.value)} /></Field>
                  <Field label={t('تعليمات للمورد — سطر لكل تعليمة', 'Instructions — one per line')} wide><LinesText dir="ltr" rows={4} disabled={locked} value={c.instructions} onChange={(v) => setC('instructions', v)} /></Field>
                  <Field label={t('حجم الصور', 'Photo size')}>
                    <select className="ac-input" disabled={locked} value={c.photoSize} onChange={(e) => setC('photoSize', e.target.value)}>
                      {PHOTO_SIZES.map((s) => <option key={s} value={s}>{{ large: t('كبير', 'Large'), medium: t('متوسط', 'Medium'), small: t('صغير', 'Small') }[s]}</option>)}
                    </select>
                  </Field>
                </div>
                <p style={{ fontSize: 11.5, color: 'var(--ac-muted)', marginTop: 10 }}>{t('لا يظهر في طلب التسعير أي سعر ولا اسم العميل.', 'An RFQ never shows a price or the client’s name.')}</p>
              </Card>
            ) : (
              <Card title={t('التسعير والإعدادات', 'Pricing & settings')}>
                <div className="ac-form-grid">
                  <Field label={t('الشكل', 'Layout')}>
                    <select className="ac-input" disabled={locked} value={doc.layout} onChange={(e) => setField('layout', e.target.value)}>
                      <option value="simple">{t('بسيط — سعر لكل بند', 'Simple — price per line')}</option>
                      <option value="itemized">{t('مفصّل — أقسام بإجمالي لكل وحدة', 'Itemized — per-unit sections')}</option>
                    </select>
                  </Field>
                  <Field label={t('عملة العرض', 'Quote currency')}>
                    <select className="ac-input" disabled={locked} value={cur} onChange={(e) => setField('currency', e.target.value)}>
                      <option value="SAR">SAR — ريال</option><option value="USD">USD — دولار</option>
                    </select>
                  </Field>
                  <Field label="Incoterm"><input className="ac-input" dir="ltr" disabled={locked} value={doc.incoterm || ''} onChange={(e) => setField('incoterm', e.target.value)} placeholder="FOB" /></Field>
                  <Field label={t('الميناء / المدينة', 'Port / city')}><input className="ac-input" disabled={locked} value={doc.port || ''} onChange={(e) => setField('port', e.target.value)} /></Field>
                  <Field label={t('عمولة الوكيل %', 'Agent commission %')}>{inputNum('agent_pct')}</Field>
                  <Field label={t('هامش معبر % (على البضائع فقط)', 'MAABAR margin % (goods only)')}>{inputNum('margin_pct')}</Field>
                  <Field label={t('سعر الصرف الداخلي USD ← SAR', 'Internal rate USD → SAR')}>{inputNum('fx_usd_sar')}</Field>
                  <Field label={t('سعر الصرف CNY ← USD', 'Rate CNY → USD')}>{inputNum('fx_cny_usd')}</Field>
                  <Field label={t('الدفعة المقدمة %', 'Deposit %')}>{inputNum('deposit_pct')}</Field>
                  <Field label={t('مدفوع سابقاً', 'Already paid')}>{inputNum('paid_amount')}</Field>
                  <Field label={t('صالح حتى', 'Valid until')}><input className="ac-input" type="date" dir="ltr" disabled={locked} value={doc.valid_until || ''} onChange={(e) => setField('valid_until', e.target.value)} /></Field>
                  <Field label={t('حجم الصور', 'Photo size')}>
                    <select className="ac-input" disabled={locked} value={c.photoSize} onChange={(e) => setC('photoSize', e.target.value)}>
                      {PHOTO_SIZES.map((s) => <option key={s} value={s}>{{ large: t('كبير', 'Large'), medium: t('متوسط', 'Medium'), small: t('صغير', 'Small') }[s]}</option>)}
                    </select>
                  </Field>
                </div>
                <p style={{ fontSize: 11.5, color: 'var(--ac-muted)', marginTop: 10 }}>
                  {t('العمولة والهامش وأسعار الصرف داخلية ولا تظهر في المستند. الهامش مدمج في الأسعار ولا يظهر كسطر مستقل.',
                    'Commission, margin and rates are internal and never appear on the document. The margin is folded into prices, never a separate line.')}
                </p>
              </Card>
            )}

            {!isRfq && (
              <Card title={t('نطاق التوريد', 'Scope of supply')}>
                <div className="ac-form-grid">
                  <Field label={t('مقدمة', 'Intro')} wide><textarea className="ac-textarea" rows={2} disabled={locked} value={c.scopeIntro} onChange={(e) => setC('scopeIntro', e.target.value)} /></Field>
                  <Field label={t('شارة (اختياري)', 'Badge (optional)')}><input className="ac-input" disabled={locked} value={c.scopeBadge} onChange={(e) => setC('scopeBadge', e.target.value)} placeholder="WSF Certified" /></Field>
                  {itemized && <Field label={t('جملة قبل جداول الأقسام', 'Sentence above the section tables')} wide><textarea className="ac-textarea" rows={2} disabled={locked} value={c.itemizedIntro} onChange={(e) => setC('itemizedIntro', e.target.value)} /></Field>}
                </div>
                {(c.scopeRows || []).map((r, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'flex-start' }}>
                    <input className="ac-input" style={{ flex: '0 0 30%' }} disabled={locked} value={r.label} placeholder={t('البند', 'Item')}
                      onChange={(e) => setC('scopeRows', c.scopeRows.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
                    <textarea className="ac-textarea" rows={1} style={{ flex: 1 }} disabled={locked} value={r.details} placeholder={t('التفاصيل', 'Details')}
                      onChange={(e) => setC('scopeRows', c.scopeRows.map((x, j) => (j === i ? { ...x, details: e.target.value } : x)))} />
                    {!locked && <button className="ac-btn" style={miniBtn} onClick={() => setC('scopeRows', c.scopeRows.filter((_, j) => j !== i))}>✕</button>}
                  </div>
                ))}
                {!locked && <button className="ac-btn ac-btn-sm" style={{ marginTop: 8 }} onClick={() => setC('scopeRows', [...(c.scopeRows || []), { label: '', details: '' }])}>{t('+ صف في جدول النطاق', '+ Scope row')}</button>}
              </Card>
            )}

            {/* sections & lines */}
            <Card title={isRfq ? t('البنود (بدون أسعار)', 'Items (no prices)') : t('البنود والأسعار', 'Lines & prices')}
              right={itemized && (
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
                  {t('عدد الوحدات', 'Units')}
                  <input className="ac-input" type="number" min={1} dir="ltr" style={{ width: 80 }} disabled={locked}
                    value={sections[0]?.units ?? 1} onChange={(e) => setUnits(e.target.value)} />
                </label>
              )}>
              {sections.map((s, si) => {
                const sl = lines.filter((l) => l.section_id === s.id);
                const showTitle = itemized || isRfq || sections.length > 1;
                const lump = s.lump_cost !== null && s.lump_cost !== undefined && s.lump_cost !== '';
                return (
                  <div key={s.id} style={{ border: '1px solid var(--ac-line)', borderRadius: 12, padding: 12, marginBottom: 12 }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
                      {showTitle && <input className="ac-input" style={{ flex: 1 }} disabled={locked} value={s.title}
                        dir={isRfq ? 'ltr' : undefined}
                        placeholder={isRfq ? 'Section (e.g. Wrestling)' : t(`عنوان القسم ${si + 1}`, `Section ${si + 1} title`)}
                        onChange={(e) => setSection(s.id, { title: e.target.value })} />}
                      {!locked && sections.length > 1 && <>
                        <button className="ac-btn" style={miniBtn} onClick={() => moveSection(s.id, -1)}>↑</button>
                        <button className="ac-btn" style={miniBtn} onClick={() => moveSection(s.id, 1)}>↓</button>
                        <button className="ac-btn" style={miniBtn} onClick={() => removeSection(s.id)}>✕</button>
                      </>}
                    </div>
                    {itemized && (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8, fontSize: 12 }}>
                        <span style={{ color: 'var(--ac-muted)' }}>{t('تكلفة إجمالية للقسم لكل وحدة (اختياري، تلغي أسعار البنود):', 'Lump cost per unit (optional, replaces line costs):')}</span>
                        <input className="ac-input" type="number" dir="ltr" style={{ width: 120 }} disabled={locked} value={s.lump_cost ?? ''}
                          onChange={(e) => setSection(s.id, { lump_cost: e.target.value, lump_currency: s.lump_currency || 'USD' })} />
                        <select className="ac-input" style={{ width: 90 }} disabled={locked || !lump} value={s.lump_currency || 'USD'} onChange={(e) => setSection(s.id, { lump_currency: e.target.value })}>
                          {COST_CURRENCIES.map((x) => <option key={x}>{x}</option>)}
                        </select>
                        {!isRfq && priced?.clientView.sections[si]?.subtotal != null && (
                          <span style={{ marginInlineStart: 'auto', fontWeight: 700 }}>{t('للوحدة', 'per unit')}: {fmtNum(priced.clientView.sections[si].subtotal)} {cur}</span>
                        )}
                      </div>
                    )}
                    <div className="ac-tablewrap" style={{ overflowX: 'auto' }}>
                      <table className="ac-table dg-lines">
                        <thead><tr>
                          <th>{t('البند', 'Item')}</th><th>{t('الوصف / المواصفات', 'Description')}</th>
                          {isRfq && <th>{t('الماركة', 'Brand')}</th>}
                          <th>{t('الكمية', 'Qty')}</th><th>{t('الوحدة', 'Unit')}</th>
                          {!isRfq && <><th>{t('سعر المورد', 'Supplier price')}</th><th>{t('العملة', 'Cur.')}</th><th>{t('النوع', 'Type')}</th>{!itemized && <th>{t('سعر العميل', 'Client price')}</th>}</>}
                          <th>{t('صور', 'Photos')}</th>
                          <th />
                        </tr></thead>
                        <tbody>
                          {sl.map((l) => {
                            const cl = !itemized && !isRfq ? priced?.clientView.sections.flatMap((x) => x.lines).find((x) => x.id === l.id) : null;
                            const bad = errors.some((e) => e.lineId === l.id);
                            return (
                              <tr key={l.id} style={bad ? { background: 'var(--ac-warn-bg)' } : undefined}>
                                <td><input className="ac-input" disabled={locked} value={l.item} onChange={(e) => setLine(l.id, { item: e.target.value })} dir={isRfq ? 'ltr' : undefined} /></td>
                                <td><textarea className="ac-textarea" rows={1} disabled={locked} value={l.description || ''} onChange={(e) => setLine(l.id, { description: e.target.value })} dir={isRfq ? 'ltr' : undefined} /></td>
                                {isRfq && <td><input className="ac-input" dir="ltr" disabled={locked} value={l.brand || ''} onChange={(e) => setLine(l.id, { brand: e.target.value })} /></td>}
                                <td><input className="ac-input" type="number" dir="ltr" style={{ width: 80 }} disabled={locked} value={l.qty ?? ''} onChange={(e) => setLine(l.id, { qty: e.target.value })} /></td>
                                <td><input className="ac-input" style={{ width: 80 }} disabled={locked} value={l.unit || ''} onChange={(e) => setLine(l.id, { unit: e.target.value })} dir={isRfq ? 'ltr' : undefined} /></td>
                                {!isRfq && <>
                                  <td><input className="ac-input" type="number" dir="ltr" style={{ width: 110 }} disabled={locked || (itemized && lump)} value={l.supplier_unit_cost ?? ''} onChange={(e) => setLine(l.id, { supplier_unit_cost: e.target.value })} /></td>
                                  <td><select className="ac-input" style={{ width: 80 }} disabled={locked || (itemized && lump)} value={l.cost_currency || ''} onChange={(e) => setLine(l.id, { cost_currency: e.target.value })}>
                                    {COST_CURRENCIES.map((x) => <option key={x}>{x}</option>)}</select></td>
                                  <td><select className="ac-input" style={{ width: 110 }} disabled={locked} value={l.line_kind || 'goods'} onChange={(e) => setLine(l.id, { line_kind: e.target.value })}>
                                    <option value="goods">{t('بضاعة (+هامش)', 'Goods (+margin)')}</option>
                                    <option value="passthrough">{t('بالتكلفة (شحن/جمارك)', 'At cost (shipping…)')}</option></select></td>
                                  {!itemized && <td dir="ltr" style={{ whiteSpace: 'nowrap', fontWeight: 700 }}>{cl?.unitPrice != null ? fmtNum(cl.unitPrice) : '—'}</td>}
                                </>}
                                <td style={{ minWidth: 120 }}>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                    {groupOf(l.id).map((im) => (
                                      <Thumb key={im.id} image={im} url={urls[im.storage_path]} size={40} locked={locked}
                                        onCrop={() => setCropping(im)} onRemove={() => removeImg(im)}>
                                        <label style={{ fontSize: 10, display: 'flex', gap: 3, alignItems: 'center', whiteSpace: 'nowrap' }}>
                                          <input type="checkbox" disabled={locked} checked={!!im.reference_only} onChange={(e) => patchImg(im, { reference_only: e.target.checked })} />
                                          {t('مرجعية', 'Ref. only')}
                                        </label>
                                      </Thumb>
                                    ))}
                                  </div>
                                  {!locked && groupOf(l.id).length < MAX_LINE_IMAGES && (
                                    <div style={{ display: 'flex', gap: 4, marginTop: 3 }}>
                                      <ImageDrop compact t={t} label={t('+ صورة', '+ Photo')} disabled={imgBusy} onFiles={(f) => addFiles(f, l.id)} />
                                      <button type="button" className="ac-btn" style={miniBtn} title={t('من مستند سابق أو من الطلب', 'From a previous document or the request')} onClick={() => setPicker({ lineId: l.id })}>…</button>
                                    </div>
                                  )}
                                </td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                  {!locked && <>
                                    <button className="ac-btn" style={miniBtn} onClick={() => moveLine(l.id, -1)}>↑</button>
                                    <button className="ac-btn" style={miniBtn} onClick={() => moveLine(l.id, 1)}>↓</button>
                                    <button className="ac-btn" style={miniBtn} onClick={() => removeLine(l.id)}>✕</button>
                                  </>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {!locked && <button className="ac-btn ac-btn-sm" style={{ marginTop: 8 }} onClick={() => addLine(s.id)}>{t('+ بند', '+ Line')}</button>}
                  </div>
                );
              })}
              {!locked && <button className="ac-btn ac-btn-sm" onClick={addSection}>{isRfq ? t('+ قسم (مثل: رياضة)', '+ Section') : t('+ قسم', '+ Section')}</button>}
            </Card>

            {!isRfq && (
              <Card title={t('نصوص جدول التسعير', 'Pricing labels')}>
                <div className="ac-form-grid">
                  <Field label={t('عنوان عمود سعر الوحدة', 'Unit price column')}><input className="ac-input" disabled={locked} value={c.unitLabel} onChange={(e) => setC('unitLabel', e.target.value)} /></Field>
                  {itemized && <Field label={t('عنوان إجمالي القسم', 'Section subtotal label')}><input className="ac-input" disabled={locked} value={c.perUnitLabel} onChange={(e) => setC('perUnitLabel', e.target.value)} /></Field>}
                  {itemized && <Field label={t('وصف سطر الملخص', 'Summary line description')} wide><input className="ac-input" disabled={locked} value={c.summaryItem} onChange={(e) => setC('summaryItem', e.target.value)} /></Field>}
                  <Field label={t('نص سطر الإجمالي (فارغ = تلقائي)', 'Total row text (empty = automatic)')} wide><input className="ac-input" disabled={locked} value={c.totalLabel} onChange={(e) => setC('totalLabel', e.target.value)} /></Field>
                  <Field label={t('سطر الضريبة', 'VAT line')} wide>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input type="checkbox" disabled={locked} checked={!!c.showVat} onChange={(e) => setC('showVat', e.target.checked)} />
                      <input className="ac-input" disabled={locked || !c.showVat} value={c.vatText} onChange={(e) => setC('vatText', e.target.value)} />
                    </div>
                  </Field>
                </div>
              </Card>
            )}

            <Card title={t('معرض الصور', 'Photo gallery')} right={<span style={{ fontSize: 11.5, color: 'var(--ac-muted)' }}>{groupOf(null).length} / {MAX_GALLERY_IMAGES}</span>}>
              <Field label={t('عنوان القسم', 'Section title')} wide>
                <input className="ac-input" disabled={locked} dir={isRfq ? 'ltr' : undefined} value={c.galleryTitle || ''} onChange={(e) => setC('galleryTitle', e.target.value)}
                  placeholder={isRfq ? 'Reference images' : t('نموذج من المشاريع المنفذة', 'Reference projects')} />
              </Field>
              {groupOf(null).map((im) => (
                <div key={im.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 10 }}>
                  <Thumb image={im} url={urls[im.storage_path]} size={72} locked={locked}
                    onCrop={() => setCropping(im)} onRemove={() => removeImg(im)} onMove={(d) => moveImg(im, d)} />
                  <div style={{ flex: 1, display: 'grid', gap: 6 }}>
                    <input className="ac-input" dir="rtl" disabled={locked} placeholder={t('التعليق بالعربية', 'Caption (Arabic)')} value={im.caption_ar || ''}
                      onChange={(e) => patchImg(im, { caption_ar: e.target.value }, false)} onBlur={(e) => patchImg(im, { caption_ar: e.target.value })} />
                    <input className="ac-input" dir="ltr" disabled={locked} placeholder={t('التعليق بالإنجليزية', 'Caption (English)')} value={im.caption_en || ''}
                      onChange={(e) => patchImg(im, { caption_en: e.target.value }, false)} onBlur={(e) => patchImg(im, { caption_en: e.target.value })} />
                  </div>
                </div>
              ))}
              {!locked && groupOf(null).length < MAX_GALLERY_IMAGES && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'stretch' }}>
                  <div style={{ flex: 1 }}><ImageDrop t={t} disabled={imgBusy} onFiles={(f) => addFiles(f, null)} /></div>
                  <button type="button" className="ac-btn" onClick={() => setPicker({ lineId: null })}>{t('من مستند سابق / الطلب', 'From previous / request')}</button>
                </div>
              )}
              {imgBusy && <div style={{ fontSize: 12, color: 'var(--ac-muted)', marginTop: 8 }}>{t('جارٍ معالجة الصور ورفعها…', 'Processing and uploading…')}</div>}
              <p style={{ fontSize: 11.5, color: 'var(--ac-muted)', marginTop: 10 }}>
                {t('تُصغَّر الصور تلقائياً إلى 1600 بكسل بصيغة JPEG وتُضمَّن داخل ملف PDF.', 'Images are resized to 1600 px JPEG and embedded in the PDF.')}
              </p>
            </Card>

            {!isRfq && (
              <Card title={t('يشمل / لا يشمل', 'Includes / excludes')}>
                <div className="ac-form-grid">
                  <Field label={t('يشمل العرض — سطر لكل نقطة', 'Includes — one per line')}><LinesText rows={4} disabled={locked} value={c.includes} onChange={(v) => setC('includes', v)} /></Field>
                  <Field label={t('لا يشمل العرض — سطر لكل نقطة', 'Excludes — one per line')}><LinesText rows={4} disabled={locked} value={c.excludes} onChange={(v) => setC('excludes', v)} /></Field>
                </div>
              </Card>
            )}

            {!isRfq ? (
              <Card title={t('الشروط', 'Terms')}>
                {(c.terms || []).map((r, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'flex-start' }}>
                    <input className="ac-input" style={{ flex: '0 0 26%' }} disabled={locked} value={r.label}
                      onChange={(e) => setC('terms', c.terms.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
                    {r.kind === 'text' ? (
                      <textarea className="ac-textarea" rows={1} style={{ flex: 1 }} disabled={locked} value={r.value || ''}
                        onChange={(e) => setC('terms', c.terms.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />
                    ) : (
                      <div style={{ flex: 1, fontSize: 12, color: 'var(--ac-muted)', padding: '9px 4px' }}>
                        {r.kind === 'deposit' ? t('يُحسب تلقائياً من الإجمالي والدفعة المقدمة والمدفوع سابقاً', 'Computed from the total, deposit % and amount paid')
                          : t('يُحسب تلقائياً من تاريخ «صالح حتى»', 'Computed from “Valid until”')}
                      </div>
                    )}
                    {!locked && <>
                      <button className="ac-btn" style={miniBtn} onClick={() => { if (i > 0) { const n = [...c.terms]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; setC('terms', n); } }}>↑</button>
                      <button className="ac-btn" style={miniBtn} onClick={() => { if (i < c.terms.length - 1) { const n = [...c.terms]; [n[i + 1], n[i]] = [n[i], n[i + 1]]; setC('terms', n); } }}>↓</button>
                      <button className="ac-btn" style={miniBtn} onClick={() => setC('terms', c.terms.filter((_, j) => j !== i))}>✕</button>
                    </>}
                  </div>
                ))}
                {!locked && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button className="ac-btn ac-btn-sm" onClick={() => setC('terms', [...c.terms, { kind: 'text', label: '', value: '' }])}>{t('+ شرط', '+ Term')}</button>
                    {!c.terms.some((x) => x.kind === 'deposit') && <button className="ac-btn ac-btn-sm" onClick={() => setC('terms', [...c.terms, { kind: 'deposit', label: 'الدفعة المقدمة' }])}>{t('+ سطر الدفعة المقدمة', '+ Deposit row')}</button>}
                    {!c.terms.some((x) => x.kind === 'validity') && <button className="ac-btn ac-btn-sm" onClick={() => setC('terms', [...c.terms, { kind: 'validity', label: 'صلاحية العرض' }])}>{t('+ سطر الصلاحية', '+ Validity row')}</button>}
                  </div>
                )}
              </Card>
            ) : (
              <Card title={t('الشروط التجارية (يملؤها المورد)', 'Commercial terms (supplier fills)')}>
                {(c.termsRows || []).map((r, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                    <input className="ac-input" dir="ltr" style={{ flex: 1 }} disabled={locked} value={r.label} onChange={(e) => setC('termsRows', c.termsRows.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
                    <input className="ac-input" dir="ltr" style={{ flex: 1 }} disabled={locked} value={r.hint} onChange={(e) => setC('termsRows', c.termsRows.map((x, j) => (j === i ? { ...x, hint: e.target.value } : x)))} />
                    {!locked && <button className="ac-btn" style={miniBtn} onClick={() => setC('termsRows', c.termsRows.filter((_, j) => j !== i))}>✕</button>}
                  </div>
                ))}
                {!locked && <button className="ac-btn ac-btn-sm" onClick={() => setC('termsRows', [...c.termsRows, { label: '', hint: '' }])}>{t('+ صف', '+ Row')}</button>}
              </Card>
            )}

            {!isRfq && (
              <Card title={t('ملاحظات', 'Notes')}>
                <div className="ac-form-grid">
                  <Field label={t('صندوق تنبيه مميز — العنوان', 'Highlighted notice — title')}><input className="ac-input" disabled={locked} value={c.notice?.title || ''} onChange={(e) => setC('notice', { ...c.notice, title: e.target.value })} /></Field>
                  <Field label={t('صندوق تنبيه مميز — النص', 'Highlighted notice — text')} wide><textarea className="ac-textarea" rows={2} disabled={locked} value={c.notice?.body || ''} onChange={(e) => setC('notice', { ...c.notice, body: e.target.value })} /></Field>
                  <Field label={t('ملاحظة — العنوان', 'Note — title')}><input className="ac-input" disabled={locked} value={c.note?.title || ''} onChange={(e) => setC('note', { ...c.note, title: e.target.value })} /></Field>
                  <Field label={t('ملاحظة — النص', 'Note — text')} wide><textarea className="ac-textarea" rows={2} disabled={locked} value={c.note?.body || ''} onChange={(e) => setC('note', { ...c.note, body: e.target.value })} /></Field>
                </div>
              </Card>
            )}
          </div>

          {/* ── PREVIEW + PROFIT ── */}
          <div className="dg-side">
            {!isRfq && iv && (
              <div className="ac-card" style={{ padding: 14, marginBottom: 12, borderInlineStart: '3px solid #8B7355' }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ac-ink)', marginBottom: 8 }}>
                  {t('لوحة الربح — داخلية، لا تظهر في المستند', 'Profit — internal, never on the document')}
                </div>
                <table className="dg-kv" dir="ltr">
                  <tbody>
                    {[
                      [t('سعر المورد', 'Supplier cost'), iv.supplierCost],
                      [t('عمولة الوكيل', 'Agent commission'), iv.agentCommission],
                      [t('التكلفة', 'Cost'), iv.cost],
                      [t('إجمالي العميل', 'Client total'), iv.clientTotal],
                      [t('ربح معبر', 'MAABAR margin'), iv.margin],
                    ].map(([label, v]) => (
                      <tr key={label}><th>{label}</th>
                        <td>{fmtNum(v.USD ?? v[cur])} USD</td>
                        <td>{fmtNum(v.SAR ?? v[cur])} SAR</td></tr>
                    ))}
                    <tr><th>{t('الهامش الفعلي', 'Effective margin')}</th>
                      <td colSpan={2}><b>{iv.marginOnCostPct ?? '—'}%</b> {t('من التكلفة', 'of cost')} · {iv.marginOnTotalPct ?? '—'}% {t('من الإجمالي', 'of total')}</td></tr>
                  </tbody>
                </table>
                {errors.length > 0 && (
                  <div style={{ marginTop: 10, fontSize: 12, color: 'var(--ac-warn)' }}>
                    <b>{t('لا يمكن الاعتماد قبل إصلاح:', 'Fix before approval:')}</b>
                    <ul style={{ margin: '4px 0 0', paddingInlineStart: 18 }}>
                      {[...new Set(errors.map((e) => e.code))].map((code) => (
                        <li key={code}>{(ERR[code] || [code, code])[isAr ? 0 : 1]} ({errors.filter((e) => e.code === code).length})</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            <div className="dg-preview" style={{ '--dg-w': `${pageW}px` }}>
              <PreviewFrame html={html} width={pageW} />
            </div>
            <p style={{ fontSize: 11, color: 'var(--ac-muted)', marginTop: 6 }}>
              {t('معاينة مباشرة. تقسيم الصفحات النهائي يظهر في ملف PDF.', 'Live preview. Final page breaks show in the PDF.')}
            </p>
          </div>
        </div>
        {toast && <div className="ac-toast">{toast}</div>}
        {approveOpen && (
          <div className="ac-modal-ov" onClick={() => !exporting && setApproveOpen(false)} style={{ zIndex: 1000 }}>
            <div className="ac-modal" onClick={(e) => e.stopPropagation()} style={{ width: 'min(560px, 94vw)' }}>
              <div className="ac-modal-title">{t('اعتماد وتصدير', 'Approve & export')}</div>
              <div style={{ fontSize: 13, lineHeight: 1.7, color: 'var(--ac-ink)' }}>
                <div>{isRfq ? t('طلب تسعير للمصنع', 'Supplier RFQ') : t('عرض سعر للعميل', 'Client quotation')}{doc.status === 'approving' && doc.ref_no ? ` — ${doc.ref_no}` : ''}</div>
                {!isRfq && priced && <div>{t('الإجمالي للعميل', 'Client total')}: <b dir="ltr">{fmtNum(priced.clientView.total)} {cur}</b></div>}
                <div style={{ color: 'var(--ac-muted)', marginTop: 6 }}>
                  {doc.status === 'approving'
                    ? t('الرقم محجوز لهذا المستند؛ ستُعاد محاولة إنشاء الملف بنفس الرقم.', 'The number is already reserved for this document; the file is re-rendered with the same number.')
                    : t('سيُعطى المستند الرقم التالي في السلسلة، ثم يُقفل. أي تعديل بعد ذلك يكون بنسخة محدثة (-R1).', 'The document takes the next number in the series and is locked. Any later change is a revision (-R1).')}
                </div>
              </div>
              <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 14, padding: 12, borderRadius: 10, background: '#F6EAD9', border: '1px solid #D8B98A', fontSize: 12.5, lineHeight: 1.6, cursor: 'pointer' }}>
                <input type="checkbox" checked={ackImages} onChange={(e) => setAckImages(e.target.checked)} style={{ marginTop: 3 }} />
                <span>
                  {isRfq
                    ? t('راجعت كل الصور والنصوص: لا تحتوي على اسم العميل أو بياناته أو أي وسيلة تواصل معه قبل الإرسال للمورد.',
                      'I checked every image and text: no client name, client details or contact information before this goes to a supplier.')
                    : t('راجعت كل الصور: لا تحتوي على اسم المورد أو شعاره أو وسائل تواصله أو علامة مائية قبل الإرسال للعميل.',
                      'I checked every image: no supplier name, logo, contact details or watermark before this goes to a client.')}
                </span>
              </label>
              {exportErr && <div className="ac-placeholder" style={{ color: 'var(--ac-danger)', marginTop: 10 }}>{exportErr}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                <button className="ac-btn" disabled={!!exporting} onClick={() => setApproveOpen(false)}>{t('إلغاء', 'Cancel')}</button>
                <button className="ac-btn ac-btn-primary" disabled={!ackImages || !!exporting} onClick={onApprove}>
                  {exporting === 'final' ? t('جارٍ الإنشاء…', 'Rendering…') : t('اعتماد وإنشاء PDF', 'Approve & create PDF')}
                </button>
              </div>
            </div>
          </div>
        )}
        {picker && (
          <PickerModal t={t} docId={doc.id} requestId={doc.request_id}
            onPickDoc={(src) => pickFromDoc(src, picker.lineId)} onPickRequest={(path) => pickFromRequest(path, picker.lineId)}
            onClose={() => setPicker(null)} />
        )}
        {cropping && (
          <CropModal t={t} url={urls[cropping.storage_path]}
            naturalAspect={cropping.width && cropping.height ? cropping.width / cropping.height : 4 / 3}
            onApply={(area) => applyCrop(cropping, area)} onClose={() => setCropping(null)} />
        )}
      </div>
    </ConsoleShell>
  );
}

// Scales an A4-wide iframe to the column's width and grows it to its content.
function PreviewFrame({ html, width }) {
  const wrap = useRef(null);
  const frame = useRef(null);
  const [scale, setScale] = useState(0.6);
  const [height, setHeight] = useState(1123);
  useEffect(() => {
    const el = wrap.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setScale(Math.min(1, el.clientWidth / width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [width]);
  const onLoad = () => {
    const d = frame.current?.contentDocument;
    if (!d) return;
    const measure = () => setHeight(Math.max(600, d.documentElement.scrollHeight));
    measure();
    d.fonts?.ready.then(measure);
    d.querySelectorAll('img').forEach((im) => im.addEventListener('load', measure));
  };
  return (
    <div ref={wrap} dir="ltr" style={{ width: '100%', height: height * scale, overflow: 'hidden', borderRadius: 10, border: '1px solid var(--ac-line)' }}>
      <iframe ref={frame} title="preview" srcDoc={html} onLoad={onLoad} sandbox="allow-same-origin"
        style={{ width, height, border: 0, transform: `scale(${scale})`, transformOrigin: 'top left', background: '#F5F3EF' }} />
    </div>
  );
}
